import { DrawerDetalleObra } from '@/components/dominio/DrawerDetalleObra';
import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { TablaExpandible } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCostosMensuales } from '@/hooks/useCostosMensuales';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import type { CostoMensual } from '@adelante/shared';
import { formatearCRC } from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, Info } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

/**
 * Vista Costos Mensuales con árbol drill-down en rows:
 *   Bloque → Sub-bloque → Proyecto
 *
 * En las cols se mantiene Año/Mes. Cada celda muestra la suma agregada
 * del nivel correspondiente.
 *
 * Sigue patrón de AreaCosteoPantalla: árbol dinámico construido desde
 * el recordset plano agrupando por niveles configurables.
 */

interface ColumnaMes {
  anio: number;
  mes: number;
  key: string;
}

interface NodoFila {
  key: string;
  label: string;
  porColumna: Map<string, number>;
  total: number;
  hijos: NodoFila[];
  nivel: number;
}

// Mismos labels que AreaCosteoPantalla:
//   bloque (BC "VB")    = "Proyecto"
//   subBloque (BC "1")  = "Bloque"
//   worksNo (BC "VB-1.01") = "Obra"
const NIVELES_ROWS: Array<{ field: keyof CostoMensual; etiqueta: string }> = [
  { field: 'bloque', etiqueta: 'Proyecto' },
  { field: 'subBloque', etiqueta: 'Bloque' },
  { field: 'worksNo', etiqueta: 'Obra' },
];

function construirArbolFilas(filas: CostoMensual[]): {
  cols: ColumnaMes[];
  raices: NodoFila[];
  totalPorCol: Map<string, number>;
  totalGeneral: number;
} {
  // Detectar columnas únicas (año/mes con data)
  const colsMap = new Map<string, ColumnaMes>();
  for (const f of filas) {
    const key = `${f.anio}-${f.mes}`;
    if (!colsMap.has(key)) colsMap.set(key, { anio: f.anio, mes: f.mes, key });
  }
  const cols = Array.from(colsMap.values()).sort((a, b) => a.anio - b.anio || a.mes - b.mes);

  // Acumular total por columna (para fila Total general)
  const totalPorCol = new Map<string, number>();
  for (const c of cols) totalPorCol.set(c.key, 0);
  for (const f of filas) {
    const key = `${f.anio}-${f.mes}`;
    totalPorCol.set(key, (totalPorCol.get(key) ?? 0) + f.totalCostoLcy);
  }
  const totalGeneral = Array.from(totalPorCol.values()).reduce((a, b) => a + b, 0);

  // Recursive grouping para construir árbol Bloque → SubBloque → Proyecto
  function recurse(filasNivel: CostoMensual[], nivel: number, pathParent: string): NodoFila[] {
    const nivelDef = NIVELES_ROWS[nivel];
    if (!nivelDef) return [];
    const field = nivelDef.field;
    const grupos = new Map<string, CostoMensual[]>();
    for (const fila of filasNivel) {
      const raw = fila[field];
      const valor = raw == null ? '(—)' : String(raw);
      if (!grupos.has(valor)) grupos.set(valor, []);
      grupos.get(valor)!.push(fila);
    }
    const nodos: NodoFila[] = [];
    for (const [valor, filasGrupo] of grupos.entries()) {
      const key = `${pathParent}/${valor}`;
      const porColumna = new Map<string, number>();
      for (const f of filasGrupo) {
        const colKey = `${f.anio}-${f.mes}`;
        porColumna.set(colKey, (porColumna.get(colKey) ?? 0) + f.totalCostoLcy);
      }
      const total = filasGrupo.reduce((acc, f) => acc + f.totalCostoLcy, 0);
      const primera = filasGrupo[0]!;
      const label = (() => {
        if (field === 'worksNo') {
          const display = primera.proyectoDisplay;
          return display && display !== valor ? `${valor} — ${display}` : valor;
        }
        return valor;
      })();
      nodos.push({
        key,
        label,
        porColumna,
        total,
        hijos: recurse(filasGrupo, nivel + 1, key),
        nivel,
      });
    }
    nodos.sort((a, b) => b.total - a.total);
    return nodos;
  }

  return { cols, raices: recurse(filas, 0, ''), totalPorCol, totalGeneral };
}

export function CostosMensualesPantalla() {
  const { data, isLoading, error } = useCostosMensuales();
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<OrdenColumna | null>(null);
  const [obraDetalle, setObraDetalle] = useState<string | null>(null);

  const arbol = useMemo(() => construirArbolFilas(data?.data ?? []), [data]);

  const toggle = (key: string) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandirTodos = () => {
    const todos = new Set<string>();
    const visitar = (nodos: NodoFila[]) => {
      for (const n of nodos) {
        if (n.hijos.length > 0) {
          todos.add(n.key);
          visitar(n.hijos);
        }
      }
    };
    visitar(arbol.raices);
    setExpandidas(todos);
  };
  const colapsarTodos = () => setExpandidas(new Set());
  const alOrdenar = (idx: number) => setOrden((actual) => siguienteOrden(actual, idx));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  if (arbol.raices.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Sin datos con los filtros seleccionados.</p>
        </CardContent>
      </Card>
    );
  }

  const aniosUnicos = Array.from(new Set(arbol.cols.map((c) => c.anio)));

  const acciones = (
    <div className="flex items-center gap-2 text-xs mr-2">
      <button type="button" onClick={expandirTodos} className="text-blue-600 hover:underline">
        Expandir todo
      </button>
      <span className="text-zinc-300">|</span>
      <button type="button" onClick={colapsarTodos} className="text-blue-600 hover:underline">
        Colapsar
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Costos Mensuales</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Árbol Proyecto → Bloque → Obra × Año/Mes. Filtros arriba. Tip: "Expandir" arriba a la
          derecha = pantalla completa.
        </p>
      </div>

      <TablaExpandible titulo="Costos Mensuales" acciones={acciones}>
        <Card className="overflow-x-auto">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 z-10 bg-background min-w-[200px]">
                    Proyecto / Bloque / Obra
                  </TableHead>
                  {aniosUnicos.map((anio) => {
                    const cnt = arbol.cols.filter((c) => c.anio === anio).length;
                    return (
                      <TableHead
                        key={anio}
                        colSpan={cnt}
                        className="text-center border-l border-zinc-200 text-xs"
                      >
                        {anio}
                      </TableHead>
                    );
                  })}
                  <TableHead className="text-right border-l border-zinc-300 bg-zinc-100 min-w-[100px]">
                    <TituloOrdenable
                      titulo="Total"
                      idx={arbol.cols.length}
                      orden={orden}
                      onOrdenar={alOrdenar}
                    />
                  </TableHead>
                </TableRow>
                <TableRow>
                  <TableHead className="sticky left-0 z-10 bg-background" />
                  {arbol.cols.map((c, idx) => (
                    <TableHead
                      key={c.key}
                      className={`text-right text-xs min-w-[80px] ${
                        idx === 0 || arbol.cols[idx - 1]?.anio !== c.anio
                          ? 'border-l border-zinc-200'
                          : ''
                      }`}
                    >
                      <TituloOrdenable
                        titulo={NOMBRES_MES[c.mes - 1] ?? ''}
                        idx={idx}
                        orden={orden}
                        onOrdenar={alOrdenar}
                      />
                    </TableHead>
                  ))}
                  <TableHead className="bg-zinc-100 border-l border-zinc-300" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordenarHermanos(arbol.raices, orden, valoresNodo(arbol.cols)).map((nodo) => (
                  <FilaArbol
                    key={nodo.key}
                    nodo={nodo}
                    cols={arbol.cols}
                    orden={orden}
                    expandidas={expandidas}
                    onToggle={toggle}
                    onVerDetalle={setObraDetalle}
                  />
                ))}
                <TableRow className="border-t-2 border-zinc-400 bg-zinc-100 font-semibold">
                  <TableCell className="sticky left-0 z-10 bg-zinc-100">Total</TableCell>
                  {arbol.cols.map((c, idx) => {
                    const v = arbol.totalPorCol.get(c.key) ?? 0;
                    return (
                      <TableCell
                        key={c.key}
                        className={`text-right text-xs tabular-nums ${
                          idx === 0 || arbol.cols[idx - 1]?.anio !== c.anio
                            ? 'border-l border-zinc-200'
                            : ''
                        }`}
                      >
                        {v === 0 ? '—' : formatearCRC(v)}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right text-xs tabular-nums bg-zinc-200 border-l border-zinc-300">
                    {formatearCRC(arbol.totalGeneral)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TablaExpandible>

      <DrawerDetalleObra worksNo={obraDetalle} onClose={() => setObraDetalle(null)} />
    </div>
  );
}

/** Valores de un nodo por columna (un mes cada una) y el Total al final, para ordenar. */
const valoresNodo =
  (cols: ColumnaMes[]) =>
  (n: NodoFila): (number | null)[] => [
    ...cols.map((c) => n.porColumna.get(c.key) || null),
    n.total || null,
  ];

function FilaArbol({
  nodo,
  cols,
  orden,
  expandidas,
  onToggle,
  onVerDetalle,
}: {
  nodo: NodoFila;
  cols: ColumnaMes[];
  orden: OrdenColumna | null;
  expandidas: Set<string>;
  onToggle: (k: string) => void;
  onVerDetalle: (worksNo: string) => void;
}) {
  const tieneHijos = nodo.hijos.length > 0;
  const abierto = expandidas.has(nodo.key);
  const indent = nodo.nivel * 16;
  const esObra = nodo.nivel === 2; // último nivel = Obra (worksNo)
  const bgPorNivel = [
    'bg-zinc-50 font-semibold', // nivel 0 = Proyecto (VB)
    'bg-white font-medium', // nivel 1 = Bloque (1)
    'bg-white', // nivel 2 = Obra (VB-1.01)
  ];

  return (
    <>
      <TableRow
        className={`${bgPorNivel[nodo.nivel] ?? ''} ${tieneHijos ? 'cursor-pointer hover:bg-zinc-100' : ''}`}
        onClick={tieneHijos ? () => onToggle(nodo.key) : undefined}
      >
        <TableCell className="sticky left-0 z-10 bg-inherit text-xs">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${indent}px` }}>
            {tieneHijos ? (
              abierto ? (
                <CaretDown className="h-3 w-3 shrink-0" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3 shrink-0" weight="bold" />
              )
            ) : (
              <span className="w-3 inline-block" />
            )}
            <span className="truncate max-w-[260px]">{nodo.label}</span>
            {esObra && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // El label puede ser "VB-1.01 — Casa..." → extraemos el code
                  const worksNo = nodo.label.split(' — ')[0]!.trim();
                  onVerDetalle(worksNo);
                }}
                title="Ver detalle de la obra"
                className="ml-1 inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                <Info className="h-3.5 w-3.5" weight="bold" />
              </button>
            )}
          </div>
        </TableCell>
        {cols.map((c, idx) => {
          const v = nodo.porColumna.get(c.key) ?? 0;
          const borderLeft =
            idx === 0 || cols[idx - 1]?.anio !== c.anio ? 'border-l border-zinc-200' : '';
          return (
            <TableCell
              key={c.key}
              className={`text-right text-xs tabular-nums ${borderLeft} ${v === 0 ? 'text-zinc-300' : ''}`}
            >
              {v === 0 ? '—' : formatearCRC(v)}
            </TableCell>
          );
        })}
        <TableCell className="text-right text-xs tabular-nums font-semibold bg-zinc-50 border-l border-zinc-300">
          {formatearCRC(nodo.total)}
        </TableCell>
      </TableRow>
      {abierto &&
        ordenarHermanos(nodo.hijos, orden, valoresNodo(cols)).map((hijo) => (
          <FilaArbol
            key={hijo.key}
            nodo={hijo}
            cols={cols}
            orden={orden}
            expandidas={expandidas}
            onToggle={onToggle}
            onVerDetalle={onVerDetalle}
          />
        ))}
    </>
  );
}
